import React, { useEffect, useState } from 'react';
import { API } from '../App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Mail, KeyRound, Lock } from 'lucide-react';
import { toast } from 'sonner';

const ForgotPasswordDialog = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1); // 1 = request, 2 = verify
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const reset = () => {
    setStep(1); setOtp(''); setNewPassword(''); setConfirmPassword(''); setLoading(false); setCooldown(0);
  };

  const errMsg = (error, fallback) => {
    const d = error?.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map((x) => x?.msg || '').filter(Boolean).join(', ') || fallback;
    if (d && typeof d === 'object') return d.msg || fallback;
    return fallback;
  };

  const requestOtp = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!email.trim()) return toast.error('Enter your email');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      toast.success(data.message || 'If an account exists, a code was sent.');
      setStep(2);
      setCooldown(60);
    } catch (error) {
      toast.error(errMsg(error, 'Could not send reset code'));
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    if (cooldown > 0) return;
    setCooldown(60);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      toast.success('A new code has been sent if the account exists.');
    } catch (error) {
      toast.error(errMsg(error, 'Could not resend code'));
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (otp.trim().length !== 6) return toast.error('Enter the 6-digit code');
    if (newPassword.length < 6) return toast.error('Password must be at least 6 characters');
    if (newPassword !== confirmPassword) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/reset-password`, {
        email: email.trim(), otp: otp.trim(), new_password: newPassword,
      });
      toast.success(data.message || 'Password reset successful');
      setOpen(false);
      reset();
    } catch (error) {
      toast.error(errMsg(error, 'Reset failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <button type="button" className="text-sm text-violet-600 hover:text-violet-700 hover:underline" data-testid="forgot-password-link">
          Forgot password?
        </button>
      </DialogTrigger>
      <DialogContent data-testid="forgot-password-dialog">
        <DialogHeader>
          <DialogTitle>Reset your password</DialogTitle>
          <DialogDescription>
            {step === 1
              ? 'Enter your account email and we\u2019ll send a 6-digit code.'
              : `Enter the code sent to ${email} and choose a new password.`}
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <form onSubmit={requestOtp} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fp-email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input id="fp-email" type="email" placeholder="your@email.com" className="pl-10"
                  value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="fp-email-input" />
              </div>
            </div>
            <Button type="submit" className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full h-11"
              disabled={loading} data-testid="fp-send-code-button">
              {loading ? 'Sending...' : 'Send reset code'}
            </Button>
          </form>
        ) : (
          <form onSubmit={submitReset} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fp-otp">6-digit code</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input id="fp-otp" inputMode="numeric" maxLength={6} placeholder="123456" className="pl-10 tracking-widest"
                  value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} required data-testid="fp-otp-input" />
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={resendOtp}
                  disabled={cooldown > 0}
                  className="text-xs text-violet-600 hover:underline disabled:text-slate-400 disabled:no-underline"
                  data-testid="fp-resend-button"
                >
                  {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="fp-newpass">New password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input id="fp-newpass" type="password" placeholder="••••••••" className="pl-10"
                  value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required data-testid="fp-newpass-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="fp-confirm">Confirm password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input id="fp-confirm" type="password" placeholder="••••••••" className="pl-10"
                  value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required data-testid="fp-confirm-input" />
              </div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <button type="button" className="text-sm text-slate-500 hover:underline" onClick={() => setStep(1)} data-testid="fp-back-button">
                &larr; Change email
              </button>
              <Button type="submit" className="bg-violet-600 hover:bg-violet-700 text-white rounded-full h-11 px-6"
                disabled={loading} data-testid="fp-reset-button">
                {loading ? 'Resetting...' : 'Reset password'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordDialog;
